import {
  Controller,
  SubmitHandler,
  useController,
  useForm,
} from 'react-hook-form';
import * as Select from '@radix-ui/react-select';
import {
  CreateQuestionChoiceInput,
  CreateQuestionInput,
  GetQuestionQuery,
  QuestionAnswerType,
  QuestionStatus,
  QuestionUsageType,
  UpdateQuestionInput,
} from '../../../apollo/__generated__/graphql.ts';
import RadioButton from '../../common/radio-button.tsx';
import { names } from '../../../utils/constants.ts';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import {
  CHOICES_FRAGMENT,
  CREATE_QUESTION,
  QUESTION_FRAGMENT,
  UPDATE_QUESTION,
} from '../../../apollo/questions.ts';
import Toggle from '../../common/toggle.tsx';
import Tooltip from '../../common/tooltip.tsx';
import { useEffect, useMemo, useState } from 'react';
import { Editor } from '../../editor/editor.tsx';
import MultipleChoices from './multiple-choices/multiple-choices.tsx';
import Button from '../../common/button.tsx';
import TagsEditor from './tags-editor.tsx';
import { toast } from 'react-toastify';
import getQuestionSelects from './questions-selects.tsx';
import { useFragment } from '../../../apollo/__generated__';
import { questionUsageTypes } from '../questions-constants.ts';
import {
  createEmptyMultipleAnswers,
  createMultipleAnswers,
  createNewMultipleAnswer,
} from '../question-utils.ts';
import QuestionContent from '../question-content/question-content.tsx';
import Modal from '../../common/modal.tsx';
import { cn, orderToLetter } from '../../../utils/common-utils.ts';
import { useNavigate } from 'react-router-dom';
import { CreateQuestionToast } from './create-question-toast.tsx';
import { useQuestionSelects } from '../questions-hooks.tsx';
import { GET_QUESTION_SOURCE_RELATION_TYPE } from '../../../apollo/question-source-relation-type.ts';
import { questionDourceRelationTypeColorOrder } from '../../../constants/question-source-relation-type-color-order.ts';

export interface IMultipleAnswer extends CreateQuestionChoiceInput {
  id: string;
}

interface IFormData extends CreateQuestionInput {
  answer: string;
}

interface ICreateQuestionForm {
  editQuestion?: GetQuestionQuery['question']['getQuestion'];
  params?: URLSearchParams;
}

const SECTION_TITLE_CLASS = 'text-base font-semibold leading-5';

const USAGE_TYPE_TOOLTIPS: Record<QuestionUsageType, string> = {
  [QuestionUsageType.Real]: 'Collegeboard official questions',
  [QuestionUsageType.Mock]: 'Questions from nalaprep and digiprep',
  [QuestionUsageType.Copy]: 'Questions from other sources',
  [QuestionUsageType.Quiz]:
    'Questions that do not simulate real test questions and do not appear on tests. These are suitable for other kinds of non-test questions, such as a vocab question with synonyms.',
};

const CreateQuestionForm = ({ editQuestion, params }: ICreateQuestionForm) => {
  const navigate = useNavigate();
  const question = useFragment(QUESTION_FRAGMENT, editQuestion);

  const [updateQuestion, { loading: updateLoading }] =
    useMutation(UPDATE_QUESTION);
  const [createQuestion, { loading: createLoading }] =
    useMutation(CREATE_QUESTION);

  const [tags, setTags] = useState<string[]>(
    question?.tags?.map((tag) => tag.name) ?? [],
  );
  const [isPreview, setIsPreview] = useState(false);
  const [isQuestionContentEditor, setQuestionContentEditor] = useState<boolean>(
    !!question?.content,
  );

  const choices = useFragment(CHOICES_FRAGMENT, question?.choices);
  const [multipleAnswers, setMultipleAnswers] = useState<IMultipleAnswer[]>([]);

  useEffect(() => {
    const answers =
      question?.answerType === QuestionAnswerType.MultipleChoice
        ? createMultipleAnswers(choices || [])
        : createEmptyMultipleAnswers();

    setMultipleAnswers(answers);
  }, [choices, question?.answerType]);

  const { data, utils } = useQuestionSelects();

  const { data: questionSourceRelationTypes } = useSuspenseQuery(
    GET_QUESTION_SOURCE_RELATION_TYPE,
    {
      errorPolicy: 'all',
    },
  );

  const isDraft = question?.status === QuestionStatus.Draft;

  const { register, handleSubmit, setValue, watch, control } =
    useForm<IFormData>({
      defaultValues: {
        usageType:
          question?.usageType ||
          (params?.get('usageType')
            ? QuestionUsageType[
                params.get('usageType') as keyof typeof QuestionUsageType
              ]
            : QuestionUsageType.Real),
        sectionId:
          Number(question?.section?.id) ||
          Number(params?.get('sectionId')) ||
          data?.question.satSections.find(
            (section) => section.name === 'reading and writing',
          )?.id ||
          data?.question.satSections[0].id,
        answerType: question?.answerType || QuestionAnswerType.MultipleChoice,
        content: question?.content || '',
        prompt: question?.prompt || '',
        domainId:
          Number(question?.domain?.id) ||
          Number(params?.get('domainId')) ||
          undefined,
        skillId:
          Number(question?.skill?.id) ||
          Number(params?.get('skillId')) ||
          undefined,
        type1Id: Number(question?.type1?.id) || undefined,
        type2Id: Number(question?.type2?.id) || undefined,
        subjectId: Number(question?.subject?.id) || undefined,
        difficultyId: question?.difficulty.id || 1,
        answer:
          question?.answerType !== QuestionAnswerType.TextEntry
            ? ''
            : choices?.[0]?.text || '',
        explanation: question?.explanation || '',
        sourceType: question?.sourceType ? question.sourceType : null,
        sourceQuestionId: question?.sourceQuestion?.originalId,
        sourceText: question?.sourceText || '',
        sourceAuthor: question?.sourceAuthor || '',
        sourceRelationTypeId: question?.sourceRelationType?.id || null,
      },
    });

  const contentInput = useController({ control, name: 'content' });
  const promptInput = useController({ control, name: 'prompt' });
  const explainInput = useController({ control, name: 'explanation' });
  const onSubmit: SubmitHandler<IFormData> = (data) => {
    let error: string | null = null;
    if (data.answerType === QuestionAnswerType.MultipleChoice) {
      if (multipleAnswers.find((answer) => !answer.text))
        error = 'Choice text is required';
      if (!multipleAnswers.find((answer) => answer.correct))
        error = 'You need to indicate which of the answers is correct';
    }
    if (!data.prompt) error = 'Question prompt is required';
    if (data.answerType === QuestionAnswerType.TextEntry && !data.answer)
      error = 'Answer is required';

    if (error) {
      toast.error(error);
      return;
    }

    const { answer, ...formData } = data;

    if (question) {
      const questionUpdateInput: UpdateQuestionInput = {
        ...formData,
        sectionId: Number(data.sectionId) || undefined,
        sourceQuestionId: data.sourceQuestionId
          ? Number(data.sourceQuestionId)
          : undefined,
        sourceRelationTypeId: Number(data.sourceRelationTypeId) || undefined,
        id: question!.id,
        tags,
        choices:
          data.answerType === QuestionAnswerType.MultipleChoice
            ? multipleAnswers.map((answer, index) => ({
                text: answer.text,
                correct: answer.correct,
                order: index,
                id: Number(answer.id),
              }))
            : [{ text: answer, order: 0, correct: true, id: choices![0].id }],
      };

      const updateMutate = updateQuestion({
        variables: {
          input: questionUpdateInput,
        },
      });
      updateMutate.catch((e) => console.error('mutate error:', e?.message));
      toast.promise(updateMutate, {
        pending: 'Saving...',
        success: 'Question updated',
      });
    } else {
      const createQuestionInput: CreateQuestionInput = {
        ...formData,
        sectionId: Number(data.sectionId) || undefined,
        sourceQuestionId: Number(data.sourceQuestionId) || undefined,
        sourceRelationTypeId: Number(data.sourceRelationTypeId) || undefined,
        sourceType: data.sourceType || undefined,
        tags,
        choices:
          data.answerType === QuestionAnswerType.MultipleChoice
            ? multipleAnswers.map((answer, index) => ({
                text: answer.text,
                correct: answer.correct,
                order: index,
              }))
            : [{ text: answer, order: 0, correct: true }],
      };
      const mutate = createQuestion({
        variables: {
          input: createQuestionInput,
        },
      });
      mutate
        .then((data) => {
          navigate(`/edit-question/${data?.data?.question?.create?.id}`);
        })
        .catch((e) => console.error('mutate error:', e?.message));
      toast.promise(mutate, {
        pending: 'Creating...',
        success: {
          autoClose: false,
          icon: false,
          render: ({ data, closeToast }) => {
            return (
              <CreateQuestionToast
                questionId={data?.data?.question?.create?.originalId}
                usageType={questionType}
                domain={domainId}
                skill={skillId}
                section={satSectionId}
                closeToast={closeToast}
              />
            );
          },
        },
      });
    }
  };

  const answerTypes = [
    QuestionAnswerType.MultipleChoice,
    QuestionAnswerType.TextEntry,
  ];

  const questionType = watch('usageType');
  const usageTypeTooltip = questionType
    ? USAGE_TYPE_TOOLTIPS[questionType]
    : '';
  const satSectionId = watch('sectionId');
  const answerType = watch('answerType');
  const domainId = watch('domainId');
  const skillId = watch('skillId');
  const answer = watch('answer');
  const subjectId = watch('subjectId');
  const sourceId = watch('sourceQuestionId');
  const sourceRelationTypeId = watch('sourceRelationTypeId');

  const selectedSourceRelationType = useMemo(
    () =>
      (
        questionSourceRelationTypes?.questionSourceRelationType.getAll ?? []
      ).find((item) => item.id === Number(sourceRelationTypeId)),
    [
      questionSourceRelationTypes?.questionSourceRelationType.getAll,
      sourceRelationTypeId,
    ],
  );

  const selectedSourceRelationTypeColors = selectedSourceRelationType
    ? questionDourceRelationTypeColorOrder[selectedSourceRelationType.name]
    : null;

  const orderedSatSections = useMemo(
    () =>
      [...(data?.question?.satSections ?? [])].sort((a, b) => {
        if (a.name === 'reading and writing') return -1;
        if (b.name === 'reading and writing') return 1;
        return 0;
      }),
    [data?.question?.satSections],
  );
  const selectedSubjectName = useMemo(
    () =>
      data?.question.subjects.find((subject) => subject.id === Number(subjectId))
        ?.name || '',
    [data?.question.subjects, subjectId],
  );
  const isSourceFieldsVisible = ['poem', 'literature'].includes(
    selectedSubjectName.trim().toLowerCase(),
  );

  const addChoice = () => {
    setMultipleAnswers((prevState) => [
      ...prevState,
      createNewMultipleAnswer(prevState.length),
    ]);
  };

  useEffect(() => {
    setQuestionContentEditor(Number(satSectionId) === 2);
  }, [satSectionId]);

  const questionInfoData = useMemo(() => {
    const getAnswer = () => {
      const ERROR_TEXT = '(Error)';

      if (answerType === QuestionAnswerType.TextEntry) return answer;
      else {
        const choice = multipleAnswers.find((answer) => answer.correct);
        return choice ? orderToLetter(choice.order) : ERROR_TEXT;
      }
    };

    return [
      {
        title: 'QID:',
        value: question?.originalId,
      },
      {
        title: 'QDOMAIN:',
        value: utils.getDomainNameById(domainId),
      },
      {
        title: 'QSKILL:',
        value: utils.getSkillNameById(skillId),
      },
      {
        title: 'ANSWER:',
        value: getAnswer(),
      },
    ];
  }, [
    answer,
    answerType,
    domainId,
    multipleAnswers,
    question?.originalId,
    skillId,
    utils,
  ]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <section>
        <div className={'mt-3 flex items-center'}>
          <h3 className={cn(SECTION_TITLE_CLASS, 'mr-2')}>UsageType*</h3>
        </div>

        <div
          className={
            'mt-2 grid grid-cols-4 gap-2 [&>div>label]:px-3 [&>div>label]:py-1.5 [&>div>label]:text-[15px] [&>div>label]:leading-5 [&>div>label>span:first-child]:truncate'
          }
        >
          {questionUsageTypes.map((label) => {
            return (
              <RadioButton
                key={label}
                label={label}
                value={label}
                checked={questionType === label}
                register={register('usageType')}
              />
            );
          })}
        </div>
        <div className="mt-3 rounded-lg border border-[#C9D7F8] bg-gradient-to-r from-[#F8FBFF] to-[#EEF4FF] px-3 py-2 shadow-sm">
          <p className="text-xs leading-5 text-[#39527F]">
            <span className="font-semibold text-[#23438A]">
              {questionType}:
            </span>{' '}
            {usageTypeTooltip}
          </p>
        </div>
      </section>

      <section>
        <h3 className={cn(SECTION_TITLE_CLASS, 'mr-2 mt-3')}>SAT Section</h3>
        <div
          className={
            'mt-2 grid grid-cols-2 gap-2 [&>div>label]:text-[15px] [&>div>label]:leading-5'
          }
        >
          {orderedSatSections.map((section) => {
            const label =
              names.satSections[
                section.name as keyof typeof names.satSections
              ] || section.name;
            return (
              <RadioButton
                key={section.id}
                value={section.id}
                label={label}
                checked={Number(satSectionId) === section.id}
                register={register('sectionId', {
                  onChange: () => {
                    setValue('domainId', undefined);
                    setValue('skillId', undefined);
                    setValue('type1Id', undefined);
                    setValue('type2Id', undefined);
                    setValue('answerType', QuestionAnswerType.MultipleChoice);
                  },
                })}
              />
            );
          })}
        </div>
      </section>

      <section className={'mt-4 grid grid-cols-3 gap-2'}>
        <div>
          <label className={SECTION_TITLE_CLASS} htmlFor="q-source-id">
            QSourceID
          </label>
          <input
            id={'q-source-id'}
            className={'base-input h-10 py-2 text-base'}
            {...register('sourceQuestionId', {
              onChange: (e) => {
                if (!e.target.value) {
                  setValue('sourceRelationTypeId', null);
                }
              },
            })}
          />
        </div>

        <div key={'QSourceRelationType'} className={'flex flex-col'}>
          <label className={SECTION_TITLE_CLASS} htmlFor="q-source-relation-type">
            QSourceRelationType
          </label>

          <Controller
            name="sourceRelationTypeId"
            control={control}
            render={({ field }) => (
              <Select.Root
                disabled={!sourceId}
                value={field.value == null ? '' : String(field.value)}
                onValueChange={(v) =>
                  field.onChange(v === '__NULL__' ? null : Number(v))
                }
              >
                <Select.Trigger
                  id="q-source-relation-type"
                  className={cn(
                    'inline-flex h-10 items-center justify-between rounded border border-light-gray px-2 py-1 text-left text-base',
                    !sourceId
                      ? 'cursor-not-allowed bg-[#F1F3F5] text-gray opacity-70'
                      : 'bg-white',
                  )}
                >
                  {selectedSourceRelationType && sourceId ? (
                    <div
                      className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-full px-3 py-1"
                      style={{
                        color: selectedSourceRelationTypeColors?.textColor,
                        backgroundColor: selectedSourceRelationTypeColors?.bgColor,
                      }}
                    >
                      <span className="truncate">
                        {selectedSourceRelationType.name}
                      </span>
                      <span
                        className="h-0 w-0 shrink-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent"
                        style={{
                          borderTopColor: selectedSourceRelationTypeColors?.textColor,
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-full bg-[#E9ECEF] px-3 py-1 text-left',
                        !sourceId && 'text-[11px] leading-4',
                      )}
                    >
                      <span className="truncate">
                        <Select.Value
                          placeholder={sourceId ? 'Empty' : 'Enter QSourceID first'}
                        />
                      </span>
                      <span className="h-0 w-0 shrink-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-black" />
                    </div>
                  )}
                </Select.Trigger>

                <Select.Content className="rounded border bg-white shadow">
                  <Select.Viewport className="p-1">
                    <Select.Item
                      style={{
                        color: '#2b2b2b',
                        backgroundColor: '#868686',
                      }}
                      value="__NULL__"
                      className="data-[highlighted]:bg-gray-100 my-2 ml-1 flex max-w-44 cursor-pointer select-none items-center gap-2 rounded px-2 py-1 outline-none"
                    >
                      <Select.ItemText>Empty</Select.ItemText>
                    </Select.Item>

                    {(
                      questionSourceRelationTypes?.questionSourceRelationType.getAll ??
                      []
                    ).map((opt, i) => {
                      const colors = questionDourceRelationTypeColorOrder[opt.name];
                      return (
                        <Select.Item
                          key={i}
                          value={String(opt.id)}
                          className="data-[highlighted]:bg-gray-100 my-2 ml-1 flex max-w-44 cursor-pointer select-none items-center gap-2 rounded px-2 py-1 outline-none"
                          style={{
                            color: colors.textColor,
                            backgroundColor: colors.bgColor,
                          }}
                        >
                          <Select.ItemText>{opt.name}</Select.ItemText>
                        </Select.Item>
                      );
                    })}
                  </Select.Viewport>
                </Select.Content>
              </Select.Root>
            )}
          />
        </div>

        <div>
          <label className={SECTION_TITLE_CLASS} htmlFor="q-source-type">
            QSourceType
          </label>
          <input
            id={'q-source-type'}
            type="text"
            className={'base-input h-10 py-2 text-base'}
            {...register('sourceType')}
          />
        </div>
      </section>

      <section className={'mt-4 grid grid-cols-2 gap-2'}>
        {getQuestionSelects(
          Number(satSectionId),
          Number(domainId),
          Number(skillId),
          data,
        ).map((select) => {
          return (
            <div key={select.label} className={'mt-4 flex flex-col'}>
              <label className={'text-xs font-semibold'}>{select.label}</label>
              <select
                className={'h-10 rounded-lg border border-light-gray'}
                defaultValue={''}
                {...register(select.registerName as keyof CreateQuestionInput, {
                  valueAsNumber: true,
                  onChange: (e) => {
                    if (e.target.name === 'domainId') {
                      setValue('skillId', undefined);
                      setValue('type1Id', undefined);
                    }
                    if (e.target.name === 'skillId') {
                      setValue('type1Id', undefined);
                    }
                  },
                })}
              >
                {select.registerName !== 'difficultyId' && (
                  <option value={''}>Choose an option</option>
                )}
                {select.options?.map((option) => (
                  <option value={option.id} key={option.id}>
                    {select.registerName === 'difficultyId'
                      ? option.id
                      : option.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </section>

      {isSourceFieldsVisible && (
        <section className={'mt-2 grid grid-cols-2 gap-2'}>
          <div className={'mt-4 flex flex-col'}>
            <div className={'flex items-center gap-1'}>
              <label className={'text-xs font-semibold'} htmlFor="source-text">
                SourceText
              </label>
              <Tooltip tip={'Name of book, play, poem, etc.'}>
                <button
                  type="button"
                  aria-label="SourceText help"
                  className={
                    'flex h-4 w-4 items-center justify-center rounded-full border border-gray text-[10px] font-semibold leading-none text-gray'
                  }
                >
                  i
                </button>
              </Tooltip>
            </div>
            <input
              id="source-text"
              type="text"
              className={'h-10 rounded-lg border border-light-gray px-1'}
              {...register('sourceText')}
            />
          </div>

          <div className={'mt-4 flex flex-col'}>
            <label className={'text-xs font-semibold'} htmlFor="source-author">
              SourceAuthor
            </label>
            <input
              id="source-author"
              type="text"
              className={'h-10 rounded-lg border border-light-gray px-1'}
              {...register('sourceAuthor')}
            />
          </div>
        </section>
      )}

      <Toggle
        label={'Question Content Editor'}
        state={isQuestionContentEditor}
        setState={setQuestionContentEditor}
        style={'mt-6'}
      />

      <section className={`mt-2 ${!isQuestionContentEditor && 'hidden'}`}>
        <Editor
          onChange={contentInput.field.onChange}
          defaultMarkdownValue={question?.content || undefined}
          className={'text-base'}
        />
        <p className={'description'}>
          You can add images by dropping it inside the editor
        </p>
      </section>

      <section className={'mt-8'}>
        <h3 className={cn(SECTION_TITLE_CLASS, 'mb-2')}>Question Prompt*</h3>
        <Editor
          onChange={promptInput.field.onChange}
          defaultMarkdownValue={question?.prompt || undefined}
          className={'text-base'}
        />
        <p className={'description'}>
          You can add images by dropping it inside the editor
        </p>
      </section>

      <section className={'mt-4'}>
        <h3 className={cn(SECTION_TITLE_CLASS, 'mr-2')}>Answer type*</h3>

        <div
          className={
            'mt-2 grid grid-cols-2 gap-2 [&>div>label]:text-[15px] [&>div>label]:leading-5'
          }
        >
          {answerTypes.map((type) => {
            return (
              <RadioButton
                key={type}
                value={type}
                label={names.answerType[type] || type}
                checked={answerType === type}
                disabled={
                  type === QuestionAnswerType.TextEntry && satSectionId == 2
                }
                register={register('answerType')}
              />
            );
          })}
        </div>
      </section>

      <section className={'mt-4'}>
        {answerType === QuestionAnswerType.MultipleChoice ? (
          <>
            <h3 className={SECTION_TITLE_CLASS}>Answer Choices*</h3>
            <MultipleChoices
              addChoice={addChoice}
              multipleAnswers={multipleAnswers}
              setMultipleAnswers={setMultipleAnswers}
            />
          </>
        ) : (
          <>
            <h3 className={SECTION_TITLE_CLASS}>Answer*</h3>
            <input
              className={'base-input text-base'}
              type={'text'}
              {...register('answer')}
            />
          </>
        )}
      </section>

      <section className={'mt-6'}>
        <h3 className={SECTION_TITLE_CLASS}>Question Explanation</h3>
        <Editor
          onChange={explainInput.field.onChange}
          defaultMarkdownValue={question?.explanation || undefined}
          className={'text-base'}
        />
        <p className={'description'}>
          You can add images by dropping it inside the editor
        </p>
      </section>

      <TagsEditor tags={tags} setTags={setTags} />

      {!!question && (
        <section
          className={cn(
            'mt-8 grid gap-3',
            isDraft ? 'grid-cols-3' : 'grid-cols-2',
          )}
        >
          <Button onClick={() => setIsPreview(true)} white>
            Preview
          </Button>
          {isDraft && (
            <Button
              onClick={handleSubmit((data) =>
                onSubmit({ ...data, status: QuestionStatus.Draft }),
              )}
              white
              type={'submit'}
            >
              Save draft
            </Button>
          )}
          <Button
            onClick={handleSubmit((data) =>
              onSubmit({ ...data, status: QuestionStatus.Published }),
            )}
            disabled={createLoading || updateLoading}
            type={'submit'}
          >
            {isDraft ? 'Publish' : 'Save'}
          </Button>
        </section>
      )}
      {!question && (
        <section className={'mt-8 grid grid-cols-3 gap-3'}>
          <Button onClick={() => setIsPreview(true)} white>
            Preview
          </Button>
          <Button
            onClick={handleSubmit((data) =>
              onSubmit({ ...data, status: QuestionStatus.Draft }),
            )}
            disabled={createLoading || updateLoading}
            white
            type={'submit'}
          >
            Save draft
          </Button>
          <Button
            onClick={handleSubmit((data) =>
              onSubmit({ ...data, status: QuestionStatus.Published }),
            )}
            disabled={createLoading || updateLoading}
            type={'submit'}
          >
            Publish
          </Button>
        </section>
      )}

      <Modal
        title={'Question preview'}
        onClose={() => setIsPreview(false)}
        isOpen={isPreview}
        fullScreen
      >
        <QuestionContent
          isExplanation={!!explainInput.field.value?.length}
          content={contentInput.field.value}
          explanation={explainInput.field.value}
          prompt={promptInput.field.value}
          isMark={false}
          isPreviewMode={true}
          choicesPreview={{
            type: answerType,
            choices:
              answerType === QuestionAnswerType.MultipleChoice
                ? multipleAnswers.map((choice) => choice.text || '')
                : [answer],
          }}
          isReportAvailable={false}
          isMath={satSectionId === 1}
          questionInfoData={questionInfoData}
        />
      </Modal>
    </form>
  );
};
export default CreateQuestionForm;
